import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/*
 * The design is authored on a 1920×1080 base canvas. Fill the CONTAINER (the
 * wallboard display full-screen, or the builder's preview pane) without
 * letterboxing: scale = min(w/1920, h/1080), and the inner canvas grows to
 * w/scale × h/scale (never smaller than 1920×1080) so extra space flows to the
 * content (rows are flex:1). We measure our own box (not the window) so it fits
 * whatever container it's dropped into.
 */

const BASE_W = 1920;
const BASE_H = 1080;

const Outer = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1 1 auto;
  align-self: stretch;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  background: var(--color-leaderboard-surface);
`;

const Inner = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;
`;

const ScalingCanvas = ({ children }: { children: React.ReactNode }) => {
  const outerRef = useRef<HTMLDivElement>(null);
  // Fallback keeps children rendering before/without measurement (e.g. jsdom in
  // tests, where clientWidth is 0 and ResizeObserver may be absent).
  const [size, setSize] = useState({ width: BASE_W, height: BASE_H });

  useEffect(() => {
    const el = outerRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (width > 0 && height > 0) {
        setSize({ width, height });
      }
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = Math.max(
    0.01,
    Math.min(size.width / BASE_W, size.height / BASE_H),
  );
  const innerW = size.width / scale;
  const innerH = size.height / scale;

  return (
    <Outer ref={outerRef}>
      <Inner
        style={{
          width: `${innerW}px`,
          height: `${innerH}px`,
          minWidth: `${BASE_W}px`,
          minHeight: `${BASE_H}px`,
          transform: `scale(${scale})`,
        }}
      >
        {children}
      </Inner>
    </Outer>
  );
};

export default ScalingCanvas;
